import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { money } from '../../api/fee'
import { useProject } from '../../layout/projectContext'
import { clearStoredSession, nav, storedSessionId, takeReturnPath } from '../../lib/checkout'
import { describeError } from '../../api/errorMessages'

/**
 * Port of `PaymentSuccessComponent` / `PaymentCancelComponent`
 * (`projects/:projectId/payment/success|cancel`) — the Stripe Checkout
 * return pages. With a stored session id: `PATCH …/sessions/{id}/check`
 * (success) or `…/expire` (cancel); then back to the page the payment was
 * started from (same-origin only), or the project dashboard.
 *
 * `/check` is called at most ONCE per visit (a ref guard — React StrictMode
 * mounts effects twice in development); the backend is idempotent anyway
 * (B5: a completed session is applied once). A failed call does not
 * redirect: the user sees the error and can retry, and the session id stays
 * stored so the retry targets the same session.
 */
export function PaymentReturnPage({ mode }: { mode: 'success' | 'cancel' }) {
  const { projectId } = useProject()
  const started = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const fallback = `/projects/${encodeURIComponent(projectId)}`
    const sessionId = storedSessionId(projectId)
    const finish = () => {
      clearStoredSession(projectId)
      nav.assign(takeReturnPath(projectId, fallback))
    }
    if (!sessionId) {
      finish()
      return
    }
    const call = mode === 'success' ? money.checkSession(projectId, sessionId) : money.expireSession(projectId, sessionId)
    call.then(finish).catch((err: unknown) => {
      started.current = false
      setError(describeError(err, 'The payment could not be confirmed'))
    })
  }, [projectId, mode, attempt])

  if (error) {
    return (
      <div className="portal-card portal-card--notice">
        <h1>{mode === 'success' ? 'Payment check failed' : 'Cancel failed'}</h1>
        <p role="alert" className="portal-notice portal-notice--error">
          {error}
        </p>
        <p>
          Your payment may still have gone through — check your{' '}
          <Link to={`/projects/${encodeURIComponent(projectId)}/payment/transactions`}>transactions</Link> before paying
          again.
        </p>
        <button
          type="button"
          onClick={() => {
            setError(null)
            setAttempt(attempt + 1)
          }}
        >
          Try again
        </button>
      </div>
    )
  }
  return (
    <div className="portal-card portal-card--notice">
      <h1>{mode === 'success' ? 'Processing payment.' : 'Canceling payment.'}</h1>
      <p>Please don&apos;t close this window.</p>
    </div>
  )
}
